import { useEffect, useRef, useCallback, useState } from 'react';

export interface UseYouTubePlayerOptions {
  autoplay: boolean;
  loop: boolean;
  mute: boolean;
}

interface UseYouTubePlayerResult {
  isReady: boolean;
  isPlaying: boolean;
  error: string | null;
  restart: () => void;
}

/**
 * Detecta si el ID corresponde a una playlist.
 * Misma lógica que en generatePlayerHTML del servicio backend.
 */
function isPlaylistId(videoId: string): boolean {
  return videoId.length > 11 || /^(PL|UU|FL|RD|LL)/.test(videoId);
}

/**
 * Agrega atributos `allow` al iframe generado por la YouTube IFrame API
 * para que el navegador permita autoplay, fullscreen, etc.
 */
function patchIframeAllow(elementId: string): void {
  const container = document.getElementById(elementId);
  const iframe = container?.tagName === 'IFRAME'
    ? container as HTMLIFrameElement
    : container?.querySelector('iframe');

  if (iframe) {
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('frameborder', '0');
  }
}

export function useYouTubePlayer(
  elementId: string,
  videoId: string | null,
  options: UseYouTubePlayerOptions
): UseYouTubePlayerResult {
  const playerRef = useRef<YTPlayer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoIdRef = useRef(videoId);
  const optionsRef = useRef(options);

  // Mantener refs actualizadas
  useEffect(() => {
    videoIdRef.current = videoId;
    optionsRef.current = options;
  }, [videoId, options]);

  const restart = useCallback(() => {
    try {
      if (playerRef.current && videoIdRef.current) {
        const vid = videoIdRef.current;
        if (isPlaylistId(vid)) {
          playerRef.current.loadPlaylist(vid);
        } else {
          playerRef.current.loadVideoById(vid);
        }
        setError(null);
      }
    } catch (e) {
      console.error('Error restarting:', e);
      window.location.reload();
    }
  }, []);

  // Bloquear manipulación del navegador
  useEffect(() => {
    // 1. Bloquear clic derecho (menú contextual)
    const blockContextMenu = (e: Event) => e.preventDefault();
    document.addEventListener('contextmenu', blockContextMenu);

    // 2. Bloquear atajos de teclado peligrosos
    const blockKeyboard = (e: KeyboardEvent) => {
      const blocked = [
        'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'F5', 'F11', 'F12', 'Escape',
      ];
      if (
        blocked.includes(e.code) ||
        (e.ctrlKey && ['u', 's', 'i', 'j', 'c'].includes(e.key.toLowerCase())) ||
        (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()))
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', blockKeyboard, true);

    // 3. Bloquear arrastrar elementos
    const blockDrag = (e: Event) => e.preventDefault();
    document.addEventListener('dragstart', blockDrag);

    // 4. Bloquear doble clic
    const blockDblClick = (e: Event) => e.preventDefault();
    document.addEventListener('dblclick', blockDblClick);

    // 5. Solicitar pantalla completa al primer gesto del usuario
    const requestFullscreen = () => {
      const el = document.documentElement;
      if (document.fullscreenElement) return;
      const request =
        el.requestFullscreen?.bind(el) ??
        (el as any).webkitRequestFullscreen?.bind(el) ??
        (el as any).msRequestFullscreen?.bind(el);
      if (request) {
        request().catch(() => {});
      }
      document.removeEventListener('click', requestFullscreen);
      document.removeEventListener('touchstart', requestFullscreen);
    };
    document.addEventListener('click', requestFullscreen);
    document.addEventListener('touchstart', requestFullscreen);

    // 6. Re-entrar a fullscreen si el usuario sale
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setTimeout(() => {
          document.documentElement.requestFullscreen?.().catch(() => {});
        }, 500);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('keydown', blockKeyboard, true);
      document.removeEventListener('dragstart', blockDrag);
      document.removeEventListener('dblclick', blockDblClick);
      document.removeEventListener('click', requestFullscreen);
      document.removeEventListener('touchstart', requestFullscreen);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  // Inicializar reproductor de YouTube
  useEffect(() => {
    if (!videoId) return;

    const isPlaylist = isPlaylistId(videoId);
    const { autoplay, loop, mute } = options;

    const loadYouTubeAPI = (): Promise<void> => {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve();
          return;
        }

        const existingScript = document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]'
        );

        if (existingScript) {
          const checkReady = setInterval(() => {
            if (window.YT && window.YT.Player) {
              clearInterval(checkReady);
              resolve();
            }
          }, 100);
          return;
        }

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          resolve();
        };
      });
    };

    const initPlayer = async () => {
      await loadYouTubeAPI();

      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }

      const element = document.getElementById(elementId);
      if (!element) {
        console.error(`Element #${elementId} not found`);
        return;
      }

      // IMPORTANTE: Si autoplay está activo, SIEMPRE iniciar muteado.
      // Los navegadores modernos (Chrome, Firefox, Safari) bloquean autoplay
      // con sonido. Una vez que el video empiece a reproducirse, se desmutea
      // automáticamente si el usuario pidió mute=0.
      const shouldForceMute = autoplay ? true : mute;

      const basePlayerVars: Record<string, string | number> = {
        autoplay: autoplay ? 1 : 0,
        mute: shouldForceMute ? 1 : 0,
        controls: 0,
        rel: 0,
        showinfo: 0,
        modestbranding: 1,
        iv_load_policy: 3,
        disablekb: 1,
        fs: 0,
        playsinline: 1,
        origin: window.location.origin,
        enablejsapi: 1,
      };

      let playerOptions: YTPlayerOptions;

      if (isPlaylist) {
        playerOptions = {
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            ...basePlayerVars,
            listType: 'playlist',
            list: videoId,
            loop: loop ? 1 : 0,
          },
          events: {
            onReady: handleReady,
            onStateChange: handleStateChange,
            onError: handleError,
          },
        };
      } else {
        playerOptions = {
          videoId: videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            ...basePlayerVars,
            ...(loop ? { loop: 1, playlist: videoId } : {}),
          },
          events: {
            onReady: handleReady,
            onStateChange: handleStateChange,
            onError: handleError,
          },
        };
      }

      playerRef.current = new window.YT.Player(elementId, playerOptions);
    };

    const handleReady = (event: YTPlayerEvent) => {
      setIsReady(true);
      setError(null);

      // Parchear iframe para permitir autoplay
      patchIframeAllow(elementId);

      if (autoplay) {
        event.target.playVideo();
      }
    };

    const handleStateChange = (event: YTPlayerEvent) => {
      switch (event.data) {
        case window.YT.PlayerState.PLAYING:
          setIsPlaying(true);
          setError(null);
          // Si el usuario pidió mute=0 pero arrancamos muteado para
          // cumplir la política de autoplay del navegador, desmutear ahora
          // que el video ya está reproduciéndose.
          if (!mute && event.target.isMuted()) {
            event.target.unMute();
            event.target.setVolume(100);
          }
          break;
        case window.YT.PlayerState.PAUSED:
          setIsPlaying(false);
          if (autoplay) {
            setTimeout(() => {
              event.target.playVideo();
            }, 300);
          }
          break;
        case window.YT.PlayerState.ENDED:
          setIsPlaying(false);
          if (loop || autoplay) {
            event.target.playVideo();
          }
          break;
        case window.YT.PlayerState.BUFFERING:
          setIsPlaying(false);
          break;
        case window.YT.PlayerState.UNSTARTED:
          // En algunos navegadores el video queda en UNSTARTED,
          // forzar play si autoplay está activo
          if (autoplay) {
            setTimeout(() => {
              event.target.playVideo();
            }, 500);
          }
          break;
      }
    };

    const handleError = (event: YTPlayerEvent) => {
      console.error('YouTube Player Error:', event.data);

      let errorMsg = '';
      switch (event.data) {
        case 2:
          errorMsg = `ID de ${isPlaylist ? 'playlist' : 'video'} inválido`;
          break;
        case 5:
          errorMsg = 'Error de reproducción HTML5';
          break;
        case 100:
          errorMsg = `${isPlaylist ? 'Playlist' : 'Video'} no encontrado o privado`;
          break;
        case 101:
        case 150:
          errorMsg = `${isPlaylist ? 'Playlist' : 'Video'} no disponible para embebido. Reintentando...`;
          setError(errorMsg);

          // Reintentar con host normal si youtube-nocookie falla
          setTimeout(() => {
            if (playerRef.current) {
              try { playerRef.current.destroy(); } catch (e) {}
              playerRef.current = null;
            }
            const el = document.getElementById(elementId);
            if (!el) return;
            // Recrear el div si fue reemplazado por iframe
            if (el.tagName === 'IFRAME') {
              const newDiv = document.createElement('div');
              newDiv.id = elementId;
              newDiv.className = 'full-screen-video';
              el.parentNode?.replaceChild(newDiv, el);
            }

            const retryVars: Record<string, string | number> = {
              autoplay: autoplay ? 1 : 0,
              mute: 1,
              controls: 0,
              rel: 0,
              showinfo: 0,
              modestbranding: 1,
              iv_load_policy: 3,
              disablekb: 1,
              fs: 0,
              playsinline: 1,
              origin: window.location.origin,
              enablejsapi: 1,
            };

            const retryOptions: YTPlayerOptions = isPlaylist
              ? {
                  host: 'https://www.youtube.com',
                  playerVars: { ...retryVars, listType: 'playlist', list: videoId, loop: loop ? 1 : 0 },
                  events: {
                    onReady: handleReady,
                    onStateChange: handleStateChange,
                    onError: (ev: YTPlayerEvent) => {
                      setError(`${isPlaylist ? 'Playlist' : 'Video'} no disponible para embebido`);
                    },
                  },
                }
              : {
                  videoId: videoId,
                  host: 'https://www.youtube.com',
                  playerVars: { ...retryVars, ...(loop ? { loop: 1, playlist: videoId } : {}) },
                  events: {
                    onReady: handleReady,
                    onStateChange: handleStateChange,
                    onError: (ev: YTPlayerEvent) => {
                      setError(`${isPlaylist ? 'Playlist' : 'Video'} no disponible para embebido`);
                    },
                  },
                };

            playerRef.current = new window.YT.Player(elementId, retryOptions);
          }, 2000);
          return;
        default:
          errorMsg = `Error: ${event.data} - Reintentando en 5 segundos...`;
      }

      setError(errorMsg);

      if (event.data !== 101 && event.data !== 150) {
        setTimeout(() => {
          if (playerRef.current) {
            const vid = videoIdRef.current;
            if (vid) {
              if (isPlaylistId(vid)) {
                playerRef.current.loadPlaylist(vid);
              } else {
                playerRef.current.loadVideoById(vid);
              }
            }
          }
        }, 5000);
      }
    };

    initPlayer();

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
      setIsReady(false);
      setIsPlaying(false);
    };
    // eslint-disable-next-line
  }, [videoId, elementId, options.autoplay, options.loop, options.mute]);

  return { isReady, isPlaying, error, restart };
}
